import { useCallback, useEffect, useState } from "react";
import { fetchPublicTrustCenter } from "../../../apis/trust-center-apis/trustCenterApis";

// A locked or older API answer omits these lists. The page reads `.length`
// and `.filter` on them, so a missing list must be an empty one.
const withLists = (data) => {
  if (!data) return data;
  return {
    ...data,
    frameworks: data.frameworks || [],
    resources: data.resources || [],
    resourceCategories: data.resourceCategories || [],
    controls: data.controls || [],
    subprocessors: data.subprocessors || [],
    faqs: data.faqs || [],
    updates: data.updates || [],
    endpoints: data.endpoints || { total: 0, encrypted: 0 },
    company: data.company
      ? { ...data.company, customLinks: data.company.customLinks || [] }
      : { name: "", customLinks: [] },
  };
};

// Loads the public Trust Center for a /trust/<slug> link. `data` is null when
// there is no published page there. `resourceId` scrolls to that resource.
// `token` is the visitor session
// (opens gated / private pages).
// Pass `preview` (already-loaded data) to skip the request: the admin preview
// reads through the signed-in API instead (see TrustCenterPreview). `null`
// means the preview is still signing in / loading: the page shows its
// skeleton, never "not available".
// refresh() fetches again in place: the page keeps showing what it has until
// the new data arrives, and keeps it if the request fails (the promise rejects).
export const useTrustCenter = (
  slug,
  { resourceId = null, preview, token = null } = {}
) => {
  const [state, setState] = useState({
    data: null,
    loading: true,
    error: false,
  });
  const skip = preview !== undefined;

  useEffect(() => {
    if (skip) return undefined;
    // No company in the link (e.g. the bare root of the Trust Center host):
    // nothing to load, the page says it is not available.
    if (!slug) {
      setState({ data: null, loading: false, error: false });
      return undefined;
    }
    let cancelled = false;
    setState({ data: null, loading: true, error: false });
    fetchPublicTrustCenter({ slug, resourceId, token })
      .then(
        (data) =>
          !cancelled &&
          setState({ data: withLists(data), loading: false, error: false })
      )
      .catch(
        () =>
          !cancelled && setState({ data: null, loading: false, error: true })
      );
    return () => {
      cancelled = true;
    };
  }, [slug, resourceId, skip, token]);

  const refresh = useCallback(async () => {
    const data = withLists(
      await fetchPublicTrustCenter({ slug, resourceId, token })
    );
    setState({ data, loading: false, error: false });
    return data;
  }, [slug, resourceId, token]);

  return skip
    ? {
        data: withLists(preview),
        loading: preview === null,
        error: false,
        refresh: async () => withLists(preview),
      }
    : { ...state, refresh };
};
